import { Button, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import MatterForm from '../sections/matter/MatterForm';
import MatterList from '../sections/matter/MatterList';

const SORT_OPTIONS = [
  { value: 'estateNameAsc', label: 'Estate Name: A-Z' }, // 🎯 New Default
  { value: 'clientAsc', label: 'Administrator: A-Z' },
  { value: 'caseNumberAsc', label: 'Case Number: Ascending' },
  { value: 'dateCreatedDesc', label: 'Date Created: Newest First' },
  { value: 'statusAsc', label: 'Status: A-Z' },
];

export default function Matters() {
  const [matters, setMatters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [sortBy, setSortBy] = useState('estateNameAsc'); // 🎯 Updated Default
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'setAsideNotProceeding' | 'all'
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [matterToDelete, setMatterToDelete] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [user, setUser] = useState(null);

  const isAdmin = user?.role === 'admin';

  // 1. Fetch current user session
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get('https://demo.insollex.com/api/user', { withCredentials: true });
        setUser(res.data);
      } catch (err) {
        console.error('Error fetching user:', err);
      }
    };
    fetchUser();
  }, []);

  // 2. Fetch Matters - Updated to show all matters to all users
  useEffect(() => {
    const fetchMatters = async () => {
      if (!user) return;
      setLoading(true);

      try {
        // 🎯 Requirement: All users can see all matters to assist with queries
        const response = await axios.get('https://demo.insollex.com/api/legal-matter', { withCredentials: true });
        const combinedMatters = response.data?.matters || [];

        if (combinedMatters.length === 0) {
          enqueueSnackbar('No matters found', { variant: 'warning' });
        }

        setMatters(combinedMatters);
      } catch (error) {
        console.error('Error fetching matters:', error);
        enqueueSnackbar('Failed to fetch matters', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchMatters();
  }, [enqueueSnackbar, user]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // 🎯 Reset to first page on search
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const handleDeleteMatter = async () => {
    try {
      await axios.delete(`https://demo.insollex.com/api/legal-matter/${matterToDelete}`);
      setMatters(matters.map((matter) => 
        matter.id === matterToDelete ? { ...matter, status: 'Deleted' } : matter
      ));
      enqueueSnackbar('Matter moved to Recycle Bin', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting matter:', error);
      enqueueSnackbar('Failed to delete matter', { variant: 'error' });
    } finally {
      setOpenDialog(false);
      setMatterToDelete(null);
    }
  };

  const handleAddMatter = (newMatter) => {
    setMatters((prev) => [newMatter, ...prev]);
    setOpenForm(false);
    enqueueSnackbar('Matter added successfully', { variant: 'success' });
  };

  const openDeleteDialog = (matterId) => {
    setMatterToDelete(matterId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setMatterToDelete(null);
  };

  // Custom sorting and filtering logic
  const statusOrder = { 'In Progress': 1, 'Pending': 2, 'Closed': 3 };

  const filteredMatters = (matters || [])
    .filter((matter) => {
      if (matter.status === 'Deleted') return false;

      const isSetAsideOrNotProceeding = matter.isSetAside || matter.isNotProceeding;
      if (statusFilter === 'active' && isSetAsideOrNotProceeding) return false;
      if (statusFilter === 'setAsideNotProceeding' && !isSetAsideOrNotProceeding) return false;

      if (searchTerm.trim() === '') return true;

      const term = searchTerm.toLowerCase();

      // 🔍 Multi-field search: Ref, Estate Name, ID, Admin
      return (
        matter?.matterName?.toLowerCase().includes(term) ||
        matter?.estateNo?.toLowerCase().includes(term) ||
        matter?.client?.toLowerCase().includes(term) ||
        matter?.idNumber?.toLowerCase().includes(term) ||
        matter?.registrationNumber?.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'estateNameAsc': // 🎯 Alphabetical Sort Requirement
          return (a.matterName || '').localeCompare(b.matterName || '');
        case 'clientAsc':
          return (a.client || '').localeCompare(b.client || '');
        case 'caseNumberAsc':
          return (a.caseNumber || 0) - (b.caseNumber || 0);
        case 'caseNumberDesc':
          return (b.caseNumber || 0) - (a.caseNumber || 0);
        case 'dateCreatedDesc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'statusAsc':
          return (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
        default:
          return 0;
      }
    });

  // Pagination logic
  const pageSize = 16;
  const paginatedMatters = filteredMatters.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const totalPages = Math.ceil(filteredMatters.length / pageSize) || 1;

  const getVisiblePageNumbers = () => {
    const maxPagesToShow = 5;
    const halfRange = Math.floor(maxPagesToShow / 2);
    let start = Math.max(1, currentPage - halfRange);
    let end = Math.min(totalPages, start + maxPagesToShow - 1);
    if (end === totalPages) start = Math.max(1, end - maxPagesToShow + 1);
    
    let pages = [];
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const statusTooltips = {
    'in progress': 'The matter is currently being worked on.',
    'pending': 'The matter is awaiting further action.',
    'closed': 'The matter has been resolved or closed.',
  };

  return (
    <Container maxWidth={false} sx={{ mb: 5 }}>
      <Typography variant="h6" sx={{ mb: 5 }}>
        Estates
      </Typography>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <TextField
          label="Search Estates"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ width: '600px' }}
        />
        <FormControl sx={{ width: '260px' }}>
          <InputLabel id="status-filter-label">Show</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            label="Show"
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          >
            <MenuItem value="active">Active Matters</MenuItem>
            <MenuItem value="setAsideNotProceeding">Set Aside / Not Proceeding</MenuItem>
            <MenuItem value="all">All Matters</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ width: '400px' }}>
          <InputLabel id="sort-by-label">Sort By</InputLabel>
          <Select
            labelId="sort-by-label"
            value={sortBy}
            label="Sort By"
            onChange={handleSortChange}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenForm(true)}
          disabled={user?.role === 'guest'}
        >
          Add Matter
        </Button>
      </Stack>

      {openForm && (
        <MatterForm
          openForm={openForm}
          setOpenForm={setOpenForm}
          onAddLegalMatter={handleAddMatter}
        />
      )}

      {loading ? (
        <CircularProgress sx={{ display: 'block', margin: 'auto' }} />
      ) : (
        <MatterList
          matters={paginatedMatters}
          onDeleteMatter={openDeleteDialog}
          isAdmin={isAdmin}
          statusTooltips={statusTooltips}
          setMatters={setMatters}
        />
      )}

      {/* Pagination Controls */}
      <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
          {'<<'}
        </Button>
        <Button variant="outlined" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
          {'<'}
        </Button>

        {getVisiblePageNumbers().map((pageNum, index) => (
          <Button
            key={index}
            variant={pageNum === currentPage ? 'contained' : 'outlined'}
            onClick={() => typeof pageNum === 'number' && setCurrentPage(pageNum)}
            disabled={typeof pageNum !== 'number'}
            sx={{ mx: 0.5 }}
          >
            {pageNum}
          </Button>
        ))}

        <Button variant="outlined" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
          {'>'}
        </Button>
        <Button variant="outlined" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
          {'>>'}
        </Button>
      </Stack>
      <Typography sx={{ mt: 2, textAlign: 'center' }}>
        Page {currentPage} of {totalPages}
      </Typography>

      {/* Confirmation Dialog for Deletion */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this matter?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteMatter} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}